<template>
  <div
    class="mx-auto flex flex-col gap-12 py-16 max-w-7xl px-4 sm:px-6 lg:px-8"
  >
    <div class="flex flex-col gap-y-8 mx-auto w-full">
      <div
        class="flex flex-col w-full mx-auto justify-center items-center max-w-[800px] text-center"
      >
        <h1
          class="text-pretty text-3xl font-medium tracking-tight text-gray-900 sm:text-4xl lg:text-balance"
        >
          Choose the Perfect Plan for Your Design Journey
        </h1>
        <p
          class="mt-4 text-lg sm:text-xl sm:leading-[30px] text-gray-700 text-pretty sm:mt-2"
        >
          Whether you're a solo designer or part of a larger team, we
          have the right plan to help you create stunning designs.
          Start with our free tier or scale up as your needs grow.
        </p>
      </div>

      <div class="flex items-center justify-center w-full">
        <div
          class="grid grid-cols-1 bg-white gap-12 lg:grid-cols-2 lg:px-8 lg:gap-0"
        >
          <div
            class="flex flex-col justify-center gap-y-10 order-2 lg:gap-y-12 lg:py-16 lg:pr-8 lg:order-1"
          >
            <div class="hidden lg:flex lg:flex-col lg:gap-y-4">
              <h2
                class="text-pretty text-3xl font-medium text-gray-900"
              >
                Share team inboxes
              </h2>
              <p class="text-lg text-gray-600 text-pretty">
                Whether you have a team of 2 or 200, our shared
                team inboxes keep everyone on the same page and
                in the loop.
              </p>
            </div>

            <div
              v-for="point in sellingPoints"
              :key="point.title"
              class="flex gap-4"
            >
              <div
                class="bg-white border border-gray-100 shadow-md p-2.5 rounded-lg h-fit"
              >
                <component
                  :is="point.icon"
                  class="size-7 stroke-[#7F56D9]"
                />
              </div>
              <div class="flex flex-col gap-2">
                <h3 class="text-xl font-medium text-gray-900">
                  {{ point.title }}
                </h3>
                <p class="text-gray-600 text-pretty">
                  {{ point.description }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col order-1 bg-white border border-gray-200 rounded-xl shadow-lg lg:order-2"
          >
            <!-- Base Subscription -->
            <div
              class="flex flex-col gap-4 p-6 border-b border-gray-200 lg:px-8"
            >
              <div
                class="flex flex-col gap-4 lg:flex-row lg:justify-between"
              >
                <div class="flex flex-col gap-4">
                  <h3
                    class="text-lg font-medium text-gray-900"
                  >
                    Base Subscription
                  </h3>
                  <div class="flex items-end gap-x-1">
                    <p
                      class="text-4xl font-semibold text-gray-900"
                    >
                      ${{ allCost }}
                    </p>
                    <p class="text-gray-600">
                      per month
                    </p>
                  </div>
                </div>
                <div class="flex items-start gap-x-2">
                  <Switch
                    id="airplane-mode"
                    v-model="isBilledYearly"
                  />
                  <label
                    for="airplane-mode"
                    class="font-medium text-gray-600"
                  >Billed Yearly</label>
                </div>
              </div>

              <p class="text-gray-600 text-pretty">
                Provides foundational tools for managing IT
                operations efficiently.
              </p>
            </div>

            <!-- Add-ons -->
            <div
              class="flex flex-col gap-6 p-6 border-b border-gray-200 cursor-pointer lg:px-8"
            >
              <div class="flex flex-col gap-1">
                <h3 class="font-semibold text-gray-900">
                  Add-ons
                </h3>
                <p class="text-gray-600">
                  Everything in our
                  <span class="font-semibold">Base subscription</span>
                  plus....
                </p>
              </div>

              <div class="flex flex-col-reverse gap-4">
                <div
                  v-for="extraAddon in extraAddons"
                  :key="extraAddon.id"
                  class="flex gap-4 justify-between p-4 border border-gray-200 rounded-xl"
                  :class="{
                    'ring-2 ring-[#7F56D9]':
                      extraAddon.is_active,
                  }"
                  @click="
                    extraAddon.is_active =
                      !extraAddon.is_active
                  "
                >
                  <div
                    class="flex flex-col gap-4 lg:flex-row"
                  >
                    <component
                      :is="extraAddon.icon"
                      class="size-8"
                    />

                    <div class="flex flex-col gap-1">
                      <h4
                        class="text-sm font-medium text-gray-700"
                      >
                        {{ extraAddon.name }}
                        <span
                          class="font-normal text-gray-600"
                        >${{
                          extraAddon.price_raw
                        }}/month</span>
                      </h4>
                      <p
                        class="text-sm text-gray-600 text-pretty"
                      >
                        {{ extraAddon.description }}
                      </p>
                    </div>
                  </div>

                  <Checkbox
                    v-model="extraAddon.is_active"
                    @click.stop
                  />
                </div>
              </div>
            </div>

            <!-- Get Started -->
            <div class="flex flex-col gap-4 p-6 lg:p-8">
              <Button @click="checkout">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef } from "vue";
import Switch from "@/Components/ui/switch/Switch.vue";
import Button from "@/Components/ui/button/Button.vue";
import Checkbox from "@/Components/ui/checkbox/Checkbox.vue";
import MagincWindIcon from "@/Components/Icons/MagicWindIcon.vue";
import FileCodeIcon from "@/Components/Icons/FileCodeIcon.vue";
import AmazonS3Icon from "@/Components/Icons/AmazonS3Icon.vue";
import GoogleMapsIcon from "@/Components/Icons/GoogleMapsIcon.vue";
import GoogleStreetViewIcon from "@/Components/Icons/GoogleStreetViewIcon.vue";
import { CheckBadgeIcon } from "@heroicons/vue/24/outline";
import { Addon } from "@/types/addon";

interface Props {
    addons: Addon[];
}

const props = defineProps<Props>();

const addonIconMapper = {
    S3: AmazonS3Icon,
    "Google Maps Directions": GoogleMapsIcon,
    "Google Maps Streetview": GoogleStreetViewIcon,
};

const extraAddons = shallowRef(getExtraAddons());
const isBilledYearly = ref(false);
const basePrice = computed(
    () => props.addons.find((addon) => addon.name === "Base")?.price_raw ?? 0
);
const allCost = computed(() =>
    extraAddons.value
        .filter((addon) => addon.is_active)
        .reduce((acc, addon) => acc + addon.price_raw, basePrice.value)
);

const sellingPoints = [
    {
        title: "Selling Point 1",
        description:
            "Whether you have a team of 2 or 200, our shared team inboxes keep everyone on the same page and in the loop.",
        icon: MagincWindIcon,
    },
    {
        title: "Selling Point 2",
        description:
            "Whether you have a team of 2 or 200, our shared team inboxes keep everyone on the same page and in the loop.",
        icon: FileCodeIcon,
    },
    {
        title: "Selling Point 3",
        description:
            "Whether you have a team of 2 or 200, our shared team inboxes keep everyone on the same page and in the loop.",
        icon: CheckBadgeIcon,
    },
];

function getExtraAddons() {
    const localAddons = props.addons.filter((addon) => addon.name !== "Base");

    return localAddons.map((addon) => {
        return {
            ...addon,
            icon: addonIconMapper[addon.name],
            description:
                "Securely store and deliver design assets for fast access.",
            is_active: false,
        };
    });
}

function checkout() {
    console.log("Checkout");
}
</script>
